import {FC} from "react";
import {IPrognoz2040} from "../../../../pages/Dashboard/Dashboard.type";
import {Title} from "../../stylesCases";
import {
  Block,
  BlockLabel,
  Card,
  ConfidenceBlock,
  ConfidenceFill,
  ConfidenceLabel,
  ConfidenceTrack,
  DriverBullet,
  DriverItem,
  DriversList,
  EvidenceItem,
  EvidenceLink,
  EvidenceList,
  EvidencePages,
  EvidenceText,
  HeaderRow,
  HorizonChip,
  MetaChip,
  MetaChips,
  Outlook,
  QuantCard,
  QuantGrid,
  QuantMetric,
  QuantNote,
  QuantUnit,
  QuantValue,
  QuantValueRow,
  RussiaText,
  Section,
} from "./styles";
import {
  formatEvidencePages,
  getConfidenceBarColor,
  getHorizonSignalStyle,
  hasPrognoz2040Content,
  hasQuantItemContent,
  resolveConfidencePercent,
} from "./utils";

interface IProps {
  data?: IPrognoz2040 | null;
}

export const Prognoz2040Section: FC<IProps> = ({data}) => {
  if (!hasPrognoz2040Content(data)) {
    return null;
  }

  const confidencePercent = resolveConfidencePercent(data);
  const confidenceLabel =
    data.model_confidence?.trim() ||
    (confidencePercent !== null
      ? `Уверенность модели: ${confidencePercent}%`
      : "");
  const horizonSignal = data.horizon_signal?.trim() || "";
  const horizonStyle = getHorizonSignalStyle(horizonSignal || undefined);
  const drivers = (
    Array.isArray(data.drivers) ? data.drivers : []
  ).filter((d) => d?.trim());
  const quant = (Array.isArray(data.quant) ? data.quant : []).filter(
    hasQuantItemContent
  );
  const evidence = (
    Array.isArray(data.evidence) ? data.evidence : []
  ).filter((e) => e?.source?.trim());
  const russiaAngle = data.russia_angle?.trim() || "";
  const outlook = data.outlook?.trim() || "";
  const showHeader = Boolean(horizonSignal) || Boolean(confidenceLabel);

  return (
    <Section>
      <Title>АНАЛИТИЧЕСКИЙ ПРОГНОЗ</Title>
      <Card>
        {showHeader && (
          <HeaderRow>
            {horizonSignal && (
              <HorizonChip
                $background={horizonStyle.background}
                $color={horizonStyle.color}
                $border={horizonStyle.border}
              >
                {horizonSignal}
              </HorizonChip>
            )}
            {confidenceLabel && (
              <ConfidenceBlock>
                <ConfidenceLabel>{confidenceLabel}</ConfidenceLabel>
                {confidencePercent !== null && (
                  <ConfidenceTrack
                    role="progressbar"
                    aria-valuenow={confidencePercent}
                    aria-valuemin={0}
                    aria-valuemax={100}
                    aria-label="Уверенность модели"
                  >
                    <ConfidenceFill
                      $width={confidencePercent}
                      $color={getConfidenceBarColor(confidencePercent)}
                    />
                  </ConfidenceTrack>
                )}
              </ConfidenceBlock>
            )}
          </HeaderRow>
        )}

        {outlook && <Outlook>{outlook}</Outlook>}

        {drivers.length > 0 && (
          <Block>
            <BlockLabel>Драйверы</BlockLabel>
            <DriversList>
              {drivers.map((driver, index) => (
                <DriverItem key={`${index}-${driver.slice(0, 24)}`}>
                  <DriverBullet />
                  <span>{driver}</span>
                </DriverItem>
              ))}
            </DriversList>
          </Block>
        )}

        {quant.length > 0 && (
          <Block>
            <BlockLabel>Цифры</BlockLabel>
            <QuantGrid>
              {quant.map((item, index) => (
                <QuantCard key={`${item.metric}-${item.year}-${index}`}>
                  {item.metric?.trim() && (
                    <QuantMetric>{item.metric}</QuantMetric>
                  )}
                  <QuantValueRow>
                    {item.value?.trim() && (
                      <QuantValue>{item.value}</QuantValue>
                    )}
                    {item.unit?.trim() && <QuantUnit>{item.unit}</QuantUnit>}
                  </QuantValueRow>
                  {(item.region?.trim() ||
                    (item.year != null && Number.isFinite(item.year))) && (
                    <MetaChips>
                      {item.region?.trim() && (
                        <MetaChip>{item.region}</MetaChip>
                      )}
                      {item.year != null && Number.isFinite(item.year) && (
                        <MetaChip>{item.year}</MetaChip>
                      )}
                    </MetaChips>
                  )}
                  {item.note?.trim() && <QuantNote>{item.note}</QuantNote>}
                </QuantCard>
              ))}
            </QuantGrid>
          </Block>
        )}

        {russiaAngle && (
          <Block>
            <BlockLabel>Контекст РФ</BlockLabel>
            <RussiaText>{russiaAngle}</RussiaText>
          </Block>
        )}

        {evidence.length > 0 && (
          <Block>
            <BlockLabel>Источники</BlockLabel>
            <EvidenceList>
              {evidence.map((item, index) => {
                const pagesLabel = formatEvidencePages(item.pages);
                const link = item.source_link?.trim() || "";
                return (
                  <EvidenceItem
                    key={`${item.source_id || item.source}-${index}`}
                  >
                    {link ? (
                      <EvidenceLink
                        href={link}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {item.source}
                      </EvidenceLink>
                    ) : (
                      <EvidenceText>{item.source}</EvidenceText>
                    )}
                    {pagesLabel && (
                      <EvidencePages>{pagesLabel}</EvidencePages>
                    )}
                  </EvidenceItem>
                );
              })}
            </EvidenceList>
          </Block>
        )}
      </Card>
    </Section>
  );
};

export default Prognoz2040Section;
